import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { addLabCSV } from "../../../services/adminServices/CoeServices";
import { toast } from "react-toastify";
import Papa from "papaparse";

const FileUploadModal = ({ open, handleClose, selectedCOE, getAllLabsData }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [hasFileError, setHasFileError] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setHasFileError(false);
    }
  }, [open]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setHasFileError(false);
  }, []);

  const downloadCSV = (content, filename = "lab_upload_status.csv") => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      setHasFileError(true);
      return;
    }

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const originalData = results.data;

        try {
          const formData = new FormData();
          formData.append("file", selectedFile);

          const response = await addLabCSV(selectedCOE, formData);
          const { inserted = [], skipped = [] } = response?.data || {};
          toast.success("Upload complete. Review the downloaded CSV for record status!");
          getAllLabsData();
          handleClose();

          const insertedMap = new Map();
          inserted.forEach(({ lab_name }) => insertedMap.set(lab_name?.trim(), true));

          const skippedMap = new Map();
          skipped.forEach(({ lab_name, message }) =>
            skippedMap.set(lab_name?.trim(), message)
          );

          const enrichedData = originalData.map((row) => {
            const labName = row.lab_name?.trim();
            if (insertedMap.has(labName)) {
              return { ...row, Status: "Inserted", Message: "" };
            } else if (skippedMap.has(labName)) {
              return { ...row, Status: "Skipped", Message: skippedMap.get(labName) };
            } else {
              return { ...row, Status: "Unknown", Message: "No match from server" };
            }
          });

          const enrichedCSV = Papa.unparse(enrichedData);
          downloadCSV(enrichedCSV);
        } catch (error) {
          toast.error(error?.response?.data?.message || error?.message || "Upload failed.");
        }
      },
      error: () => {
        toast.error("Error parsing CSV file.");
      }
    });
  }, [selectedFile, selectedCOE, getAllLabsData, handleClose]);

  const boxStyles = {
    width: 460,
    bgcolor: "#fff",
    boxShadow: 24,
    borderRadius: 3,
    p: 4,
    mx: "auto",
    mt: "25vh",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    transition: "all 0.3s ease-in-out",
  };

  const uploadBtnStyles = {
    justifyContent: "space-between",
    borderRadius: 2,
    padding: "14px 18px",
    fontSize: "15px",
    borderColor: hasFileError ? "#d32f2f" : "grey.400",
    color: hasFileError ? "#d32f2f" : "text.primary",
    textTransform: "none",
    width: "100%",
    backgroundColor: "#fafafa",
    "&:hover": {
      borderColor: "primary.main",
      backgroundColor: "#f1f1f1",
    },
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
      <Box sx={boxStyles}>
        <Typography variant="h6" fontWeight={600}>
          📤 Upload Lab CSV
        </Typography>

        <Box>
          <Button
            variant="outlined"
            component="label"
            endIcon={<UploadFileIcon />}
            sx={uploadBtnStyles}
          >
            {selectedFile ? selectedFile.name : "Choose a CSV File *"}
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileChange}
            />
          </Button>
          {hasFileError && (
            <Typography color="error" variant="caption" sx={{ pl: 1, pt: 0.5 }}>
              Please select a CSV file.
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            textTransform: "none",
            fontSize: "16px",
            py: 1.4,
            borderRadius: 2,
          }}
          onClick={handleSubmit}
        >
          🚀 Submit
        </Button>
      </Box>
    </Modal>
  );
};

export default FileUploadModal;
